import { useState } from 'react';
import PropTypes from 'prop-types'
import { withRouter } from 'next/router';
import Head from 'next/head';
import compose from 'lodash/flowRight';
import { graphql } from '@apollo/client/react/hoc';
import Loading from '../../../components/loading';
import ErrorMessage from '../../../components/error-message';
import NotFound from '../../../components/not-found';
import LoginRequired from '../../../components/login-required';
import { groupMembersQuery } from '../../../apollo/groups';
import { buildTitle } from '../../../utils/layout';
import GroupHeader from '../../../components/groups/header';
import VerticalTabs from '../../../components/vertical-tabs';
import MembersTab from '../../../components/groups/members-tab';
import InvitesTab from '../../../components/groups/invites-tab';
import consumeAppContext from '../../../consumers/app';
import config from '../../../config';
import { buildImageUrl } from '../../../utils/images';
import Main from '../../../components/main';

const GroupMembers = ({ loading, error, group }) => {
  const [currentTab, setCurrentTab] = useState('members');

  if (error) {
    return <ErrorMessage message='An error occurred while loading group.' error={error} />;
  }
  if (loading) { return <Loading />; }
  if (!group || group.found) { return <NotFound />; }
  if (!group.permissions.accessible) {
    return <LoginRequired />;
  }

  return (
    <Main>
      <Head>
        <title>{buildTitle(`Members of ${group.name}`)}</title>
        <meta name="robots" content="noindex" />
        <link rel="canonical" href={`${config().universal.baseUri}/groups/${group.slug}/members`} />
        <meta property="og:url" content={`${config().universal.baseUri}/groups/${group.slug}/members`} />
        <meta property="og:title" content={`Members of ${group.name}`} />
        {group.image && (
          <meta property="og:image" content={buildImageUrl(group.image, 'facebook-share')} />
        )}
        <meta
          name="description"
          content={`Browse members of the local cycling group ${group.name} in ${group.city}`}
        />
      </Head>

      <GroupHeader group={group} tab="members" />

      {group.permissions.editable ? (
        <VerticalTabs
          tabs={[
            { label: 'Members', tab: 'members' },
            { label: 'Invites', tab: 'invites' }
          ]}
          currentTab={currentTab}
          onChange={(e, newTab) => setCurrentTab(newTab)}
        >
          <VerticalTabs.TabPanel tab="members" currentTab={currentTab}>
            <MembersTab group={group} />
          </VerticalTabs.TabPanel>

          <VerticalTabs.TabPanel tab="invites" currentTab={currentTab}>
            <InvitesTab group={group} />
          </VerticalTabs.TabPanel>
        </VerticalTabs>
      ) : (
        <MembersTab group={group} />
      )}
    </Main>
  );
};

GroupMembers.propTypes = {
  router: PropTypes.object,
  group: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.object
};

export default compose(
  withRouter,
  consumeAppContext,
  graphql(groupMembersQuery, {
    props: ({ data: { group, loading, error } }) => {
      return {
        group,
        loading,
        error
      };
    },
    options: ({ router }) => ({
      variables: {
        slug: router.query.slug
      }
    })
  })
)(GroupMembers);
